import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { AliasBadge, StatusBadge, VerificationBadge } from '../components/Badges';
import { ArrowLeft, Lock, ShoppingCart, MapPin, Clock, Tag, Shield, AlertCircle, CheckCircle2, MessageSquare, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import LeadPassport from '../components/LeadPassport';

export default function LeadDetailPage() {
  const { leadId } = useParams();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [fullData, setFullData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchLead();
  }, [leadId]);

  const fetchLead = async () => {
    try {
      const res = await api.get(`/leads/${leadId}/preview`);
      setLead(res.data);

      // Try fetch full data if purchased
      if (user) {
        try {
          const fullRes = await api.get(`/leads/${leadId}/full`);
          setFullData(fullRes.data.full_data);
        } catch (e) {
          // Not purchased yet
        }
      }
    } catch (e) {
      toast.error('Lead not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!user) {
      toast.error('Please sign in to purchase');
      navigate('/login');
      return;
    }
    if (user.role !== 'buyer') {
      toast.error('Only buyers can purchase leads');
      return;
    }

    setPurchasing(true);
    try {
      // New P0 flow: single Stripe checkout for lead purchase
      // Backend will gate by membership status
      const origin = window.location.origin;
      const checkoutRes = await api.post(`/leads/${leadId}/purchase`, { origin_url: origin });

      // Redirect to Stripe
      window.location.href = checkoutRes.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail || 'Purchase failed';
      
      // Check if membership error
      if (detail.includes('membership') || detail.includes('subscription')) {
        toast.error('Active $99/month membership required', {
          action: {
            label: 'Subscribe',
            onClick: () => navigate('/membership')
          }
        });
      } else {
        toast.error(detail);
      }
      setPurchasing(false);
    }
  };

  const handleOpenRoom = async () => {
    if (!user) {
      toast.error('Please sign in to ask questions');
      navigate('/login');
      return;
    }
    try {
      const res = await api.post(`/leads/${leadId}/room`);
      if (res.data.room_id) {
        navigate(`/rooms/${res.data.room_id}`);
      } else if (res.data.rooms && res.data.rooms.length > 0) {
        navigate(`/rooms/${res.data.rooms[0].room_id}`);
      } else {
        toast.info('No active rooms for this listing yet');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open room');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="text-center py-24 text-gray-500 font-mono text-sm">Loading lead details...</div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate('/marketplace')}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-6"
          data-testid="lead-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to marketplace
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-3">
              <AliasBadge alias={lead.seller_alias} />
              <StatusBadge status={lead.status} />
              <VerificationBadge level={lead.verification_level} />
            </div>
            <div className="font-mono text-sm text-gray-400 mb-2" data-testid="lead-id">{lead.lead_id}</div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">{lead.category}</h1>

            {lead.digital_title && (
              <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid="digital-title-row">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0055FF]/5 border border-[#0055FF]/20 rounded font-mono text-xs text-[#0044CC]">
                  <Shield className="w-3.5 h-3.5" />
                  Digital Title · {lead.digital_title}
                </div>
                <button
                  onClick={() => {
                    const url = `${window.location.origin}/l/${lead.digital_title}`;
                    navigator.clipboard?.writeText(url);
                    toast.success('Public passport link copied');
                  }}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-600 hover:text-[#0044CC] transition-colors"
                  data-testid="share-public-passport-btn"
                >
                  <Share2 className="w-3.5 h-3.5" />
                  Share public passport
                </button>
              </div>
            )}

            {/* Masked preview */}
            <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-gray-500 font-mono mb-4">
                {fullData ? 'Full Lead Data' : 'Masked Preview'}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                <Field icon={Tag} label="Category" value={lead.category} />
                <Field icon={MapPin} label="Region" value={lead.region} />
                <Field icon={Clock} label="Lead Age" value={`${lead.lead_age_hours} hours`} />
                <Field icon={Shield} label="Source Type" value={lead.source_type} />
              </div>

              {lead.masked_preview && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-3">Preview Details</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    {Object.entries(lead.masked_preview).map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-gray-500 capitalize">{k.replace(/_/g, ' ')}</span>
                        <span className="text-gray-900 font-medium">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {fullData && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-green-700 mb-3">
                    <CheckCircle2 className="w-4 h-4" />
                    Unlocked Full Contact Data
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                    {Object.entries(fullData).map(([k, v]) => (
                      <div key={k} className="flex justify-between border-b border-gray-100 pb-2">
                        <span className="text-gray-500 capitalize">{k.replace(/_/g, ' ')}</span>
                        <span className="text-gray-900 font-medium font-mono">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Source info */}
            <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3" data-testid="non-circumvention-notice">
              <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <div className="font-medium">Non-circumvention applies.</div>
                <div className="mt-1 text-amber-800">
                  Any buyer, seller, lead source or transaction introduced through LeadXchange must continue through LeadXchange for the defined period.
                </div>
              </div>
            </div>

            {/* Lead Passport */}
            {user && (
              <div className="mt-6">
                <LeadPassport leadId={leadId} />
              </div>
            )}
          </div>

          {/* Purchase panel */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-6 sticky top-24">
              <div className="font-mono text-3xl font-semibold text-gray-900" data-testid="lead-price">
                ${lead.price.toFixed(2)}
              </div>
              <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">Exclusive Lead</div>

              {!fullData ? (
                (user && (lead.seller_alias === user.alias || user.role === 'admin')) ? (
                  <div className="mt-6 rounded border border-gray-200 bg-gray-50 p-4" data-testid="owner-view-notice">
                    <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">Your listing</div>
                    <div className="text-sm text-gray-700">
                      {user.role === 'admin'
                        ? 'Admin view — buyer actions hidden.'
                        : 'This is your own lead. Purchase and Deal Room actions are only shown to buyers.'}
                    </div>
                    <div className="mt-3 text-xs text-gray-500 flex gap-2">
                      <Lock className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      <span>Full contact details are private until a buyer completes a settled purchase.</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <Button
                      onClick={handlePurchase}
                      disabled={purchasing || lead.status !== 'available'}
                      className="w-full mt-6 h-12 bg-[#0055FF] hover:bg-[#0044CC] text-white"
                      data-testid="purchase-btn"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      {purchasing ? 'Processing...' : lead.status !== 'available' ? 'Not Available' : 'Buy Lead'}
                    </Button>
                    <Button
                      onClick={handleOpenRoom}
                      variant="outline"
                      className="w-full mt-2 h-11 border-gray-300"
                      data-testid="open-room-btn"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Ask in Deal Room
                    </Button>
                    <div className="mt-4 text-xs text-gray-500 flex gap-2">
                      <Lock className="w-3 h-3 flex-shrink-0 mt-0.5" />
                      <span>Full contact details unlock immediately after successful payment.</span>
                    </div>
                  </>
                )
              ) : (
                <>
                  <div className="mt-4 bg-green-50 border border-green-200 rounded p-3 flex gap-2 text-xs text-green-800">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>You have purchased this lead. Full contact data is shown.</span>
                  </div>
                  <Button
                    onClick={handleOpenRoom}
                    variant="outline"
                    className="w-full mt-3 h-11 border-gray-300"
                    data-testid="open-room-btn"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Open Deal Room
                  </Button>
                </>
              )}

              <div className="mt-6 pt-6 border-t border-gray-100 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Settlement</span>
                  <span className="text-gray-900 font-medium">Marketplace Payout</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Dispute window</span>
                  <span className="text-gray-900 font-medium">48 hours</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Payment</span>
                  <span className="text-gray-900 font-medium">Stripe</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2">
    <Icon className="w-4 h-4 text-gray-400 mt-0.5" />
    <div>
      <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-sm text-gray-900 font-medium mt-0.5">{value}</div>
    </div>
  </div>
);
